import { TimeEntry } from "models/TimeEntry";
import { createContext, useContext, useEffect } from "react";

type TimeEntriesContextProps = {
  children: React.ReactNode;
  data: TimeEntry[] | undefined;
  selectedEntries: string[];
  setSelectedEntries: (entryIds: string[]) => void;
};

type TimeEntriesContextType = {
  entries: TimeEntry[];
  selectedEntries: string[];
  setSelectedEntries: (entryIds: string[]) => void;
  toggleSelection: (entryId: string) => void;
};

const TimeEntriesContext = createContext<TimeEntriesContextType | undefined>(undefined);

const useTimeEntriesContext = () => {
  const context = useContext(TimeEntriesContext);
  if (!context) {
    throw new Error("useTimeEntriesContext must be used within an TimeEntriesProvider");
  }
  return context;
};

const TimeEntriesProvider: React.FC<TimeEntriesContextProps> = ({
  children,
  data,
  selectedEntries,
  setSelectedEntries,
}) => {
  // Clear selection whenever data changes
  useEffect(() => {
    setSelectedEntries([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  function toggleSelection(entryId: string) {
    if (selectedEntries.includes(entryId)) {
      setSelectedEntries(selectedEntries.filter((id) => id !== entryId));
    } else {
      setSelectedEntries([...selectedEntries, entryId]);
    }
  }

  return (
    <TimeEntriesContext.Provider
      value={{
        entries: data || [],
        selectedEntries,
        setSelectedEntries,
        toggleSelection,
      }}
    >
      {children}
    </TimeEntriesContext.Provider>
  );
};

export { TimeEntriesProvider as default, useTimeEntriesContext };
